<script lang="ts" setup>
import { ref, watch } from 'vue';

const props = defineProps<{
  ready?: boolean;
}>();

const root = ref<HTMLElement>();
let containerHeight = 0;

const enter = (element: Element): void => {
  const rootEl = root.value;
  if (!rootEl) return;
  rootEl.style.height = `${containerHeight}px`;

  // Run the animation only in case of readiness.
  // E.g.: WB needs some time to load the script chunks and render the component, so don't trigger the height animation before.
  // Necessary by the first load of a component when everything isn't yet in the browser cache.
  const unwatch = watch(
    () => props.ready,
    (ready) => {
      if (!ready) return;
      rootEl.style.height = `${element.clientHeight}px`;
      unwatch();
    },
    { immediate: true },
  );
};

const afterEnter = (): void => {
  if (!root.value) return;
  root.value.style.height = '';
};

const beforeLeave = (element: Element): void => {
  containerHeight = element.clientHeight;
};
</script>

<template>
  <div ref="root" class="owpi-crossfade-transition">
    <transition name="owpi-crossfade-transition" @enter="enter" @after-enter="afterEnter" @before-leave="beforeLeave">
      <slot></slot>
    </transition>
  </div>
</template>

<style lang="scss">
.owpi-crossfade-transition {
  position: relative;
  transition: var(--wb-fade) height 0.6s;

  &-leave-active {
    transition: var(--wb-fade) opacity 0.6s;
    overflow: hidden;
  }

  &-enter-active {
    transition: var(--wb-fade) opacity 0.6s 0.3s;
    overflow: hidden;
    position: absolute;
    top: 0;
    inset-inline-start: 50%;
    transform: translateX(-50%);
  }

  &-enter-from,
  &-leave-to {
    opacity: 0;
  }
}
</style>
